import React, { useEffect } from 'react'

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    useTheme,
    makeStyles,
    IconButton,
    CircularProgress,
    Typography,
    Chip,
    Box,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'

import { useIssueAnalytics } from '../../../../hooks/useIssueAnalytics'
import { LeaseStatusFilterState } from '../../../../hooks/useLeaseFilter'
import { LeaseStatusType } from '../../../../models'
import IssueCharts from '../../../../components/IssueAnalytics/IssueCharts'
import { Container } from '../../../../components/Container'

const useStyles = makeStyles((theme) => ({
    analyticsDashboard: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '24px',
        padding: '20px',
        height: 'calc(100vh - 200px)',
        overflow: 'hidden',
    },
    chartContainer: {
        flex: '1 1 calc(50% - 24px)',
        minWidth: '400px',
        background: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        height: '500px',
        '& h3': {
            marginTop: 0,
            marginBottom: '16px',
        },
        '& .js-plotly-plot': {
            flex: 1,
        },
    },
    dialogTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        width: '100%',
        padding: theme.spacing(2, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
        position: 'relative',
    },
    titleSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        flex: 1,
    },
    statusChips: {
        display: 'flex',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    closeButton: {
        position: 'absolute',
        top: theme.spacing(1),
        right: theme.spacing(1),
    },
}))

interface IssueAnalyticsModalProps {
    open: boolean
    statusFilter: LeaseStatusFilterState
    onClose: () => void
}

export const IssueAnalyticsModal: React.FC<IssueAnalyticsModalProps> = ({
    open,
    statusFilter,
    onClose,
}) => {
    const theme = useTheme()
    const classes = useStyles()

    const {
        getIssueAnalytics,
        issueAnalytics,
        loadingState,
    } = useIssueAnalytics()

    // Get the lease status names for display
    const getLeaseStatusName = (status: LeaseStatusType): string => {
        const statusNames: Record<number, string> = {
            1: 'Applicant',
            2: 'Cancelled',
            3: 'Future',
            4: 'Current',
            5: 'Notice',
            6: 'Past',
        }
        return statusNames[status] || `Status ${status}`
    }

    // Get selected statuses for display
    const selectedStatuses = Object.keys(statusFilter)
        .map((key) => Number(key) as LeaseStatusType)
        .filter((status) => statusFilter[status])
        .map((status) => getLeaseStatusName(status))

    useEffect(() => {
        if (open) {
            let statusStr = ''
            Object.keys(statusFilter).forEach((key) => {
                const status: LeaseStatusType = Number(key) as LeaseStatusType
                if (statusFilter[status]) {
                    statusStr += `${status},`
                }
            })
            getIssueAnalytics(statusStr)
        }
    }, [open])

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle className={classes.dialogTitle}>
                <Box className={classes.titleSection}>
                    <Typography
                        variant="h5"
                        component="h2"
                        style={{ fontWeight: 'bold' }}
                    >
                        Issue Analytics
                    </Typography>
                    {selectedStatuses.length > 0 && (
                        <Box className={classes.statusChips}>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{ marginRight: theme.spacing(1) }}
                            >
                                Filtering by:
                            </Typography>
                            {selectedStatuses.map((status, index) => (
                                <Chip
                                    key={index}
                                    label={status}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    )}
                </Box>
                <IconButton onClick={onClose} className={classes.closeButton}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent style={{ padding: 0, overflow: 'hidden' }}>
                {loadingState.mainPage ? (
                    <Container
                        style={{
                            flex: 1,
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '400px',
                        }}
                    >
                        <CircularProgress size={50} />
                    </Container>
                ) : (
                    issueAnalytics && (
                        <IssueCharts
                            analytics={issueAnalytics}
                            loadingState={loadingState}
                        />
                    )
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    )
}
